import React from 'react';

/**
 * ErrorBoundary — catches uncaught render-time errors in any descendant.
 *
 * Wrap each route element in this so a single broken component doesn't
 * blank the entire shell. Falls back to a polite "something went wrong"
 * panel with a retry button + a hint to check the browser console (in
 * dev) or contact support (in prod).
 *
 * React requires this to be a class component (no hook equivalent).
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Log to console for now — surface to a real APM (Sentry, etc) later.
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary] caught render error:', error, info);
    }

    reset = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            const isDev = process.env.NODE_ENV !== 'production';
            return (
                <div className="max-w-xl mx-auto mt-12 px-4">
                    <div className="bg-white rounded-2xl shadow-card border border-red-100 p-8 text-center">
                        <div className="w-14 h-14 mx-auto rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mb-4">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-1">Something went wrong</h2>
                        <p className="text-sm text-gray-500 mb-5">
                            This page hit an unexpected error. The rest of Olli AI is unaffected — try the
                            button below to reload this view, or use the menu to navigate elsewhere.
                        </p>
                        {isDev && this.state.error?.message && (
                            <pre className="text-left text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 overflow-auto max-h-48">
                                {this.state.error.message}
                                {this.state.error.stack ? '\n\n' + this.state.error.stack : ''}
                            </pre>
                        )}
                        <div className="flex items-center justify-center gap-2">
                            <button
                                onClick={this.reset}
                                className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-semibold text-sm hover:shadow-md"
                            >
                                Reload page
                            </button>
                            <a
                                href="/"
                                className="px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl font-medium text-sm hover:bg-gray-50"
                            >
                                Go to dashboard
                            </a>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
